import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useEarlyPredictionsQuery,
  useHistoricalResultsQuery,
} from "@/api/vercel";
import { faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  format,
  isWithinInterval,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { useEarlyMailForRace } from "@/hooks/useEarlyMailForRace";
import { useHotTipSettings } from "@/hooks/useHotTipSettings";
import { trackTipHistoryClick } from "@/utils/analytics";
import { logger } from "@/utils/logger";
import { getStoredProductName } from "@/utils/product";
import RenderTips from "@/utils/render-tips";
import { hasRaceResults, tallyResultsForToday } from "@/utils/tally-results";
import { transformRaceData } from "@/utils/transform-race-data";
import { triggerUpgradeFlow } from "@/utils/upgrade";
import { PRODUCT_LEXI_SOCIAL } from "@/utils/vars";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  ChevronRight,
  Frown,
  Lock,
} from "lucide-react";
import EarlyMailRaceSection from "@/components/EarlyMailRaceSection";
import TipMovementKey from "@/components/TipMovementKey";
import { SHOW_TIP_MARKER_KEY } from "@/utils/tip-movement";
import ErrorBoundary from "@/components/ErrorBoundary";
import Legend from "@/components/Legend";
import RaceNavbar from "@/components/RaceNavbar";
import ResultsTally from "@/components/ResultsTally";
import BottomNavbar from "@/components/bottom-navbar";
import CustomSwitch from "@/components/custom-switch";
import FaceMehIcon from "@/components/icons/face-meh-icon";
import FaceSmileIcon from "@/components/icons/face-smile-icon";
import FlagIcon from "@/components/icons/flag-icon";
import FlameIcon from "@/components/icons/flame-icon";
import MedalIcon from "@/components/icons/medal-icon";
import TrackIcon from "@/components/icons/track-icon";
import TrophyIcon from "@/components/icons/trophy-icon";
import TrackRaceSelector from "@/components/track-race-selector";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import DatePicker from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import "../../../src/styles/_date-filter-container.scss";
import "../../../src/styles/_results.scss";

export default function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const [timePeriod, setTimePeriod] = useState("today");
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedTracks, setExpandedTracks] = useState({});
  const [expandedRaces, setExpandedRaces] = useState({});
  const [trackResults, setTrackResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [selectedRace, setSelectedRace] = useState(null);
  const [trackData, setTrackData] = useState({});
  const isInitialRender = useRef(true);
  const [hasHistoricalData, setHasHistoricalData] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isChanging, setIsChanging] = useState(false);
  const [isTipHistoryExpanded, setIsTipHistoryExpanded] = useState(false);
  // Early Mail sits in the same container as the race tips (as on /todays-tips).
  // Starts OPEN here (a review page; owner 2 Nov) and keeps its state across races.
  const [isEarlyMailOpen, setIsEarlyMailOpen] = useState(true);
  // RenderTips reports whether movement markers are shown (for the marker key)
  const [isTipMovementShown, setIsTipMovementShown] = useState(false);
  const [trackDataRefresh, setTrackDataRefresh] = useState(0);
  const { hotTipMinPrice, isLoading: isHotTipSettingsLoading } =
    useHotTipSettings();
  // Add state variables for RaceNavbar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availableDates, setAvailableDates] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [trackMap, setTrackMap] = useState(new Map());
  const [dialValue, setDialValue] = useState(0);
  const [races, setRaces] = useState([]);
  const [isTrackSelectOpen, setIsTrackSelectOpen] = useState(false);

  // Add a new state variable to track specifically if we have no data for today
  const [noDataForCurrentPeriod, setNoDataForCurrentPeriod] = useState(false);
  // Add a ref to track if we've already flagged no data to prevent infinite updates
  const noDataFlaggedRef = useRef(false);

  // Add a new state variable to track the current view
  const [viewMode, setViewMode] = useState("all-tracks"); // "all-tracks", "track-races", "single-race"

  // Add state to track recently viewed dates with valid data
  const [recentDates, setRecentDates] = useState([]);
  const MAX_RECENT_DATES = 10; // Increased from 3 to 7 to show more dates

  // State for extended history functionality
  const [historyDaysLoaded, setHistoryDaysLoaded] = useState(7); // Start with 7 days
  const [loadingMoreDays, setLoadingMoreDays] = useState(false);
  const [loadingDayIndex, setLoadingDayIndex] = useState(0); // Track which day is currently loading

  // Add a ref for the date filter container
  const dateFilterContainerRef = useRef(null);

  // Add a new ref to track data processing state
  const dataProcessedRef = useRef(false);

  // Sort toggle state variables
  const [sortType, setSortType] = useState("popularity"); // popularity, a-z, winners, places, both
  const [sortDirection, setSortDirection] = useState("asc"); // asc, desc
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [productName, setProductName] = useState(
    () => getStoredProductName() || "",
  );
  const isLexiSocial =
    (productName || "").toLowerCase() === PRODUCT_LEXI_SOCIAL.toLowerCase();

  // Sort toggle functions
  const getTrackWinsAndPlaces = (trackName) => {
    // Use the same logic as ResultsTally component
    const tally = tallyResultsForToday(historicalData, 2);

    // Return the track data from the tally, or default to 0
    if (tally.byTrack[trackName]) {
      return {
        wins: tally.byTrack[trackName].wins,
        places: tally.byTrack[trackName].places,
      };
    }

    return { wins: 0, places: 0 };
  };

  const sortTracks = (tracks, type, direction) => {
    // logger.log(`🔄 Sorting tracks by ${type} (${direction})`, { tracksCount: tracks.length });

    const sorted = [...tracks].sort((a, b) => {
      let comparison = 0;

      switch (type) {
        case "popularity":
          // For popularity, we keep the original order (no sorting needed)
          return 0;

        case "a-z":
          comparison = a.name.localeCompare(b.name);
          break;

        case "winners":
          const aWins = getTrackWinsAndPlaces(a.name).wins;
          const bWins = getTrackWinsAndPlaces(b.name).wins;
          comparison = aWins - bWins; // Lower wins first by default (asc), will be flipped for desc
          break;

        case "places":
          const aPlaces = getTrackWinsAndPlaces(a.name).places;
          const bPlaces = getTrackWinsAndPlaces(b.name).places;
          comparison = aPlaces - bPlaces; // Lower places first by default (asc), will be flipped for desc
          break;

        case "both":
          const aTotal =
            getTrackWinsAndPlaces(a.name).wins +
            getTrackWinsAndPlaces(a.name).places;
          const bTotal =
            getTrackWinsAndPlaces(b.name).wins +
            getTrackWinsAndPlaces(b.name).places;
          comparison = aTotal - bTotal; // Lower total first by default (asc), will be flipped for desc
          break;

        default:
          return 0;
      }

      // Apply direction (except for popularity which doesn't have direction)
      if (type !== "popularity" && direction === "desc") {
        comparison = -comparison;
      }

      return comparison;
    });

    // logger.log(`✅ Sorted tracks by ${type} (${direction}):`, sorted.map(track => {
    //   const stats = getTrackWinsAndPlaces(track.name);
    //   return { name: track.name, wins: stats.wins, places: stats.places };
    // }));

    return sorted;
  };

  const handleSortChange = (selectedType) => {
    setIsDropdownOpen(false); // Close dropdown

    if (selectedType === sortType && selectedType !== "popularity") {
      // Toggle direction if same type is selected (except popularity)
      const newDirection = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(newDirection);
      logger.log(`🔁 Toggled ${selectedType} direction to: ${newDirection}`);
    } else {
      // Change to new type
      setSortType(selectedType);
      // Set default direction based on type
      const defaultDirection =
        selectedType === "popularity"
          ? "asc"
          : selectedType === "a-z"
            ? "asc"
            : "desc"; // Start with asc for all types
      setSortDirection(defaultDirection);
      logger.log(`🎯 Sort changed to: ${selectedType} (${defaultDirection})`);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest(".sort-dropdown")) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Track product changes for gating
  useEffect(() => {
    const handleProductChange = () => {
      try {
        setProductName(getStoredProductName() || "");
      } catch {
        setProductName("");
      }
    };

    window.addEventListener("lexi:product-updated", handleProductChange);
    window.addEventListener("storage", handleProductChange);
    handleProductChange();

    return () => {
      window.removeEventListener("lexi:product-updated", handleProductChange);
      window.removeEventListener("storage", handleProductChange);
    };
  }, []);

  const getSortDisplayLabel = (type) => {
    const baseLabels = {
      popularity: "Popularity",
      "a-z": "Alphabetical",
      winners: "Wins",
      places: "Places",
      both: "Total",
    };

    return baseLabels[type] || "Sort";
  };

  const getSortArrowIcon = (type, direction, isSelected) => {
    if (type === "popularity") return null;

    if (isSelected) {
      return direction === "asc" ? (
        <ArrowUp className="w-3 h-3 text-[#F30099]" />
      ) : (
        <ArrowDown className="w-3 h-3 text-[#F30099]" />
      );
    } else {
      return <ArrowUpDown className="w-3 h-3 opacity-50" />;
    }
  };

  // Helper function to scroll active date button into view
  const scrollActiveDateIntoView = useCallback(() => {
    setTimeout(() => {
      if (dateFilterContainerRef.current) {
        const activeButton = dateFilterContainerRef.current.querySelector(
          ".bg-\\[\\#F30099\\]",
        );
        if (activeButton) {
          activeButton.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
            inline: "center",
          });
        }
      }
    }, 50); // Small timeout to ensure DOM updates
  }, []);

  // Handle time period button click
  const handleTimePeriodChange = useCallback(
    (period) => {
      // Reset the "no data flagged" ref when changing time periods
      noDataFlaggedRef.current = false;
      setTimePeriod(period);
      // Reset any selections
      setSelectedTrack(null);
      setSelectedRace(null);
      // Maintain "All Tracks" in breadcrumbs when changing time periods
      setBreadcrumbs([{ name: "All Tracks", path: "/results" }]);
      setExpandedTracks({});
      setExpandedRaces({});
      // Reset the no data flag when changing time periods
      setNoDataForCurrentPeriod(false);
      // Reset view mode to all-tracks
      setViewMode("all-tracks");

      // Scroll active date into view
      scrollActiveDateIntoView();
    },
    [scrollActiveDateIntoView],
  );

  // New function to handle time changes (including custom dates)
  const handleTimeChange = useCallback(
    (period, customDate = null) => {
      // Reset the "no data flagged" ref when changing time periods
      noDataFlaggedRef.current = false;
      setTimePeriod(period);

      // If it's a custom date, set the selectedDate
      if (period === "custom" && customDate) {
        setSelectedDate(customDate);
      }

      // Reset any selections
      setSelectedTrack(null);
      setSelectedRace(null);
      // Maintain "All Tracks" in breadcrumbs when changing time periods
      setBreadcrumbs([{ name: "All Tracks", path: "/results" }]);
      setExpandedTracks({});
      setExpandedRaces({});
      // Reset the no data flag when changing time periods
      setNoDataForCurrentPeriod(false);
      // Reset view mode to all-tracks
      setViewMode("all-tracks");

      // Scroll active date into view
      scrollActiveDateIntoView();
    },
    [scrollActiveDateIntoView],
  );

  // Add the toggleTipHistory function
  const toggleTipHistory = () => {
    const nextState = !isTipHistoryExpanded;
    setIsTipHistoryExpanded(nextState);
    trackTipHistoryClick({
      isExpanded: nextState,
      pageContext: "results",
      path: `${location.pathname}${location.search}`,
    });
  };

  // Function to load more history days
  const loadMoreDays = useCallback(async () => {
    if (loadingMoreDays) return; // Prevent multiple simultaneous loads

    setLoadingMoreDays(true);
    setLoadingDayIndex(0);

    // Simulate loading each day with delay for visual feedback
    for (let i = 0; i < 7; i++) {
      setLoadingDayIndex(i + 1);
      // Small delay to show loading animation for each day
      await new Promise((resolve) => setTimeout(resolve, 200));
    }

    // Update the number of loaded days
    setHistoryDaysLoaded((prev) => prev + 7);
    setLoadingMoreDays(false);
    setLoadingDayIndex(0);
  }, [loadingMoreDays]);

  // Handle date change for RaceNavbar
  const handleDateChange = (newDate, position = null) => {
    // Reset the "no data flagged" ref when changing dates
    noDataFlaggedRef.current = false;
    setSelectedDate(newDate);

    // Update timePeriod based on the new date
    const today = new Date();
    today.setHours(0, 0, 0, 0); // normalize to start of day

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const normalizedNewDate = new Date(newDate);
    normalizedNewDate.setHours(0, 0, 0, 0); // normalize to start of day

    if (normalizedNewDate.getTime() === today.getTime()) {
      setTimePeriod("today");
    } else if (normalizedNewDate.getTime() === yesterday.getTime()) {
      setTimePeriod("yesterday");
    } else {
      // For dates before yesterday, keep as a custom date
      // but we still want the timePeriod to reflect it's not today/yesterday
      setTimePeriod("custom");
    }

    // Reset the no data flag when changing dates
    setNoDataForCurrentPeriod(false);

    // Reset selections when date changes
    setSelectedTrack(null);
    setSelectedRace(null);
    // Maintain "All Tracks" in breadcrumbs when changing dates
    setBreadcrumbs([{ name: "All Tracks", path: "/results" }]);

    // Reset view mode to all-tracks
    setViewMode("all-tracks");

    // Scroll active date into view
    scrollActiveDateIntoView();
  };

  // Listen for route changes to update track data when navigating back to results
  useEffect(() => {
    if (
      location.pathname === "/results" ||
      location.pathname === "/app/results"
    ) {
      // Update the refresh counter to trigger the track data refresh
      setTrackDataRefresh((prev) => prev + 1);
    }
  }, [location.pathname]);

  // Fetch track data from JSON file
  useEffect(() => {
    fetch("/data/horseracing/tracks/track-names.json")
      .then((response) => response.json())
      .then((data) => {
        setTrackData(data);
        // If we already have results, update their states with the new track data
        if (trackResults.length > 0) {
          const updatedTrackResults = trackResults.map((track) => ({
            ...track,
            state:
              data && track.name
                ? getTrackStateFromData(track.name, data)
                : track.state || "",
          }));
          setTrackResults(updatedTrackResults);
          setFilteredResults(updatedTrackResults);
        }
      })
      .catch((error) => {
        logger.error("Error loading track data:", error);
      });
  }, [trackDataRefresh, trackResults.length]);

  const handleTrackChange = (value) => {
    setIsChanging(true);
    setIsMounted(false);
    setSelectedTrack(value);
    if (trackMap.has(value)) {
      const trackRaces = Array.from(trackMap.get(value)).sort((a, b) => a - b);
      setRaces(trackRaces);
      setSelectedRace(trackRaces[0]?.toString() || "");
      setDialValue(trackMap.get(value).size);
    }
    setIsTrackSelectOpen(false);
    setTimeout(() => {
      setIsChanging(false);
      setIsMounted(true);
    }, 300);
  };

  const handleRaceChange = (value) => {
    setIsChanging(true);
    setIsMounted(false);
    setSelectedRace(value);
    setTimeout(() => {
      setIsChanging(false);
      setIsMounted(true);
    }, 300);
  };

  // Handle track selection
  const selectTrack = (track) => {
    setSelectedTrack(track);
    setBreadcrumbs([
      { name: "All Tracks", path: "/results" },
      { name: `${track.name}`, path: null },
    ]);
    // Set view mode to track-races
    setViewMode("track-races");
    // Reset race selection
    setSelectedRace(null);
  };

  // Handle race selection
  const selectRace = (track, race) => {
    setSelectedRace(race);
    setBreadcrumbs([
      { name: "All Tracks", path: "/results" },
      { name: `${track.name}`, path: null, onClick: () => selectTrack(track) },
      { name: `Race ${race.number}`, path: null },
    ]);
    // Set view mode to single-race
    setViewMode("single-race");
  };
  const handleRaceNavigation = useCallback(
    (direction) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // normalize to start of day

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (viewMode === "all-tracks") {
        // In all-tracks view, navigate between days
        if (direction === "prev") {
          // Go to previous day - always possible to go back in time
          if (timePeriod === "today") {
            handleTimePeriodChange("yesterday");
          } else if (timePeriod === "yesterday" || timePeriod === "custom") {
            // Create date for previous day based on current selectedDate
            const previousDay = new Date(selectedDate);
            previousDay.setDate(previousDay.getDate() - 1);
            handleDateChange(previousDay);
          }
        } else if (direction === "next") {
          // Go to next day if not on today
          const normalizedSelectedDate = new Date(selectedDate);
          normalizedSelectedDate.setHours(0, 0, 0, 0);

          if (normalizedSelectedDate.getTime() < today.getTime()) {
            // Selected date is before today, so we can go forward
            if (normalizedSelectedDate.getTime() === yesterday.getTime()) {
              // If it's yesterday, go to today
              handleTimePeriodChange("today");
            } else if (timePeriod === "custom") {
              // Otherwise, calculate the next day
              const nextDay = new Date(selectedDate);
              nextDay.setDate(nextDay.getDate() + 1);

              // If next day is yesterday, use the yesterday shortcut
              const nextDayNormalized = new Date(nextDay);
              nextDayNormalized.setHours(0, 0, 0, 0);

              if (nextDayNormalized.getTime() === yesterday.getTime()) {
                handleTimePeriodChange("yesterday");
              } else if (nextDayNormalized.getTime() === today.getTime()) {
                // If next day is today, use the today shortcut
                handleTimePeriodChange("today");
              } else {
                // Otherwise just go to the next day as a custom date
                handleDateChange(nextDay);
              }
            }
          }
        }
      } else if (viewMode === "track-races") {
        // In track overview, navigate between tracks
        const currentTrackIndex = filteredResults.findIndex(
          (track) => track.name === selectedTrack.name,
        );

        if (direction === "next") {
          // Move to next track, loop to first track if at the end
          if (currentTrackIndex < filteredResults.length - 1) {
            const nextTrack = filteredResults[currentTrackIndex + 1];
            selectTrack(nextTrack);
          } else if (filteredResults.length > 0) {
            // Loop back to the first track
            const firstTrack = filteredResults[0];
            selectTrack(firstTrack);
          }
        } else {
          // Move to previous track, loop to last track if at the beginning
          if (currentTrackIndex > 0) {
            const prevTrack = filteredResults[currentTrackIndex - 1];
            selectTrack(prevTrack);
          } else if (filteredResults.length > 0) {
            // Loop back to the last track
            const lastTrack = filteredResults[filteredResults.length - 1];
            selectTrack(lastTrack);
          }
        }
      } else if (viewMode === "single-race") {
        // Get current track index and races
        const currentTrackIndex = filteredResults.findIndex(
          (track) => track.name === selectedTrack.name,
        );
        const currentRaceNumber = parseInt(
          selectedRace?.number || selectedRace,
        );

        if (direction === "next") {
          // Try to move to next race in current track
          const currentTrackRaces = selectedTrack.races
            .map((r) => parseInt(r.number))
            .sort((a, b) => a - b);
          const currentRaceIndex = currentTrackRaces.indexOf(currentRaceNumber);

          if (currentRaceIndex < currentTrackRaces.length - 1) {
            // Move to next race in current track
            const nextRaceNumber = currentTrackRaces[currentRaceIndex + 1];
            const nextRace = selectedTrack.races.find(
              (r) => parseInt(r.number) === nextRaceNumber,
            );
            setSelectedRace(nextRace);
            setBreadcrumbs([
              { name: "All Tracks", path: "/results" },
              {
                name: `${selectedTrack.name}`,
                path: null,
                onClick: () => selectTrack(selectedTrack),
              },
              { name: `Race ${nextRace.number}`, path: null },
            ]);
            setViewMode("single-race");
          } else if (currentTrackIndex < filteredResults.length - 1) {
            // Move to first race of next track
            const nextTrack = filteredResults[currentTrackIndex + 1];
            if (nextTrack && nextTrack.races.length > 0) {
              const firstRace = nextTrack.races[0];
              setSelectedTrack(nextTrack);
              setSelectedRace(firstRace);
              setBreadcrumbs([
                { name: "All Tracks", path: "/results" },
                {
                  name: `${nextTrack.name}`,
                  path: null,
                  onClick: () => selectTrack(nextTrack),
                },
                { name: `Race ${firstRace.number}`, path: null },
              ]);
              setViewMode("single-race");
            }
          } else {
            // We're at the last race of the last track
            // Check if we're not on today's data - if so, loop back to the first race of the first track
            const isToday = timePeriod === "today";

            if (!isToday && filteredResults.length > 0) {
              // Loop back to the first track, first race
              const firstTrack = filteredResults[0];
              if (firstTrack && firstTrack.races.length > 0) {
                const firstRace = firstTrack.races[0];
                setSelectedTrack(firstTrack);
                setSelectedRace(firstRace);
                setBreadcrumbs([
                  { name: "All Tracks", path: "/results" },
                  {
                    name: `${firstTrack.name}`,
                    path: null,
                    onClick: () => selectTrack(firstTrack),
                  },
                  { name: `Race ${firstRace.number}`, path: null },
                ]);
                setViewMode("single-race");
              }
            }
            // If we are on today's data, do nothing - button will be disabled
          }
        } else {
          // Try to move to previous race in current track
          const currentTrackRaces = selectedTrack.races
            .map((r) => parseInt(r.number))
            .sort((a, b) => a - b);
          const currentRaceIndex = currentTrackRaces.indexOf(currentRaceNumber);
          const minRaceNumber = Math.min(...currentTrackRaces);

          if (currentRaceNumber === minRaceNumber) {
            // If we're at the first race of the current track, move to the last race of the previous track
            if (currentTrackIndex > 0) {
              const prevTrack = filteredResults[currentTrackIndex - 1];
              if (prevTrack && prevTrack.races.length > 0) {
                const lastRace = prevTrack.races[prevTrack.races.length - 1];
                setSelectedTrack(prevTrack);
                setSelectedRace(lastRace);
                setBreadcrumbs([
                  { name: "All Tracks", path: "/results" },
                  {
                    name: `${prevTrack.name}`,
                    path: null,
                    onClick: () => selectTrack(prevTrack),
                  },
                  { name: `Race ${lastRace.number}`, path: null },
                ]);
                setViewMode("single-race");
              }
            } else {
              // If at the first race of the first track, loop to the last race of the last track
              const lastTrack = filteredResults[filteredResults.length - 1];
              if (lastTrack && lastTrack.races.length > 0) {
                const lastRace = lastTrack.races[lastTrack.races.length - 1];
                setSelectedTrack(lastTrack);
                setSelectedRace(lastRace);
                setBreadcrumbs([
                  { name: "All Tracks", path: "/results" },
                  {
                    name: `${lastTrack.name}`,
                    path: null,
                    onClick: () => selectTrack(lastTrack),
                  },
                  { name: `Race ${lastRace.number}`, path: null },
                ]);
                setViewMode("single-race");
              }
            }
          } else if (currentRaceIndex > 0) {
            // Move to previous race in current track
            const prevRaceNumber = currentTrackRaces[currentRaceIndex - 1];
            const prevRace = selectedTrack.races.find(
              (r) => parseInt(r.number) === prevRaceNumber,
            );
            setSelectedRace(prevRace);
            setBreadcrumbs([
              { name: "All Tracks", path: "/results" },
              {
                name: `${selectedTrack.name}`,
                path: null,
                onClick: () => selectTrack(selectedTrack),
              },
              { name: `Race ${prevRace.number}`, path: null },
            ]);
            setViewMode("single-race");
          }
        }
      }
    },
    [
      viewMode,
      timePeriod,
      handleTimePeriodChange,
      tracks,
      selectedTrack,
      selectedRace,
      filteredResults,
      handleDateChange,
      selectedDate,
      scrollActiveDateIntoView,
    ],
  );

  // Handle track accordion toggle
  const toggleTrack = (trackName) => {
    if (selectedTrack) return; // Don't toggle if we're in detail view

    setExpandedTracks((prev) => ({
      ...prev,
      [trackName]: !prev[trackName],
    }));
  };

  // Handle race accordion toggle
  const toggleRace = (trackName, raceNumber) => {
    if (selectedRace) return; // Don't toggle if we're in race detail view

    setExpandedRaces((prev) => ({
      ...prev,
      [`${trackName}-${raceNumber}`]: !prev[`${trackName}-${raceNumber}`],
    }));
  };

  // Calculate date range based on selected time period
  const getDateRange = () => {
    const today = new Date();

    switch (timePeriod) {
      case "yesterday":
        return { start: subDays(today, 1), end: subDays(today, 1) };
      case "today":
      default:
        return { start: today, end: today };
    }
  };

  // Format date for API query
  const formatDateForQuery = (date) => {
    return format(date, "dd-MM-yyyy");
  };

  // Get query parameters based on current time period
  const getQueryParams = () => {
    const today = new Date();

    // Use a simple date parameter with the date in DD-MM-YYYY format
    if (timePeriod === "today") {
      return {
        date: formatDateForQuery(today),
        fetchAll: false,
      };
    } else if (timePeriod === "yesterday") {
      return {
        date: formatDateForQuery(subDays(today, 1)),
        fetchAll: false,
      };
    } else if (timePeriod === "custom") {
      // For dates before yesterday, use the selectedDate
      return {
        date: formatDateForQuery(selectedDate),
        fetchAll: false,
      };
    } else if (timePeriod === "all") {
      return {
        fetchAll: true,
      };
    } else {
      // For other time periods (week, month, year), use today's date for now
      return {
        date: formatDateForQuery(today),
        fetchAll: false,
      };
    }
  };

  const queryParams = useMemo(
    () => getQueryParams(),
    // getQueryParams depends on `timePeriod` and `selectedDate`
    [timePeriod, selectedDate],
  );

  // Get historical results data with date filtering
  const {
    data: histResults = { data: {} },
    isError,
    isLoading,
    refetch,
  } = useHistoricalResultsQuery(queryParams);

  // Safely get the historical data with a default empty object
  const historicalData = histResults?.data || {};

  // Get early predictions data for the same date
  const {
    data: earlyPredictions = { data: {} },
    isLoading: isEarlyPredictionsLoading,
  } = useEarlyPredictionsQuery(queryParams);

  // Safely get the early predictions data
  const earlyPredictionsData = earlyPredictions?.data || {};

  // Refetch data when time period changes
  useEffect(() => {
    refetch();
  }, [timePeriod, refetch]);

  // Early Mail (future-tips) for the selected race, shown under its tips.
  // Lexi Social only sees it once the race has results.
  const canShowEarlyMail =
    Boolean(queryParams?.date && selectedTrack && selectedRace) &&
    (!isLexiSocial ||
      hasRaceResults(
        selectedTrack.name,
        selectedRace.number,
        queryParams.date,
        historicalData,
      ));
  const earlyMail = useEarlyMailForRace({
    date: queryParams?.date,
    trackName: selectedTrack?.name,
    raceNumber: selectedRace?.number,
    viewedDate: selectedRace?.date || selectedDate,
    enabled: canShowEarlyMail,
  });

  // Tip movement markers (src/utils/tip-movement.js): Early Mail feeds the race tips'
  // markers, and the race tips (Race Day + Late Mail) feed the Early Mail markers.
  const selectedRaceKey =
    selectedTrack?.name && selectedRace?.number && selectedRace?.date
      ? `${selectedTrack.name.replace(/\s+/g, "-")}-${selectedRace.number}-${formatDateForQuery(selectedRace.date)}`
      : null;
  const raceTipMovementSources = useMemo(
    () => ({ earlyMail: canShowEarlyMail ? earlyMail.earlyMailTips : null }),
    [canShowEarlyMail, earlyMail.earlyMailTips],
  );
  // Mirrors the race tips below: Race Day tips only when the archived race has them
  const earlyMailTipMovementSources = useMemo(() => {
    const archivedRaceDay = selectedRace?.data?.earlyPredictionData;
    const hasRaceDay =
      Array.isArray(archivedRaceDay?.firstFourData) &&
      archivedRaceDay.firstFourData.length > 0;
    return {
      raceDay: hasRaceDay
        ? (selectedRaceKey && earlyPredictions?.data?.[selectedRaceKey]) ||
          archivedRaceDay
        : null,
      latest: selectedRace?.data?.predictionData || null,
    };
  }, [selectedRaceKey, earlyPredictions?.data, selectedRace]);
  // Lexi Social: no markers until the race has results (never leak a locked set)
  const hideTipMovement =
    isLexiSocial &&
    !(
      selectedRaceKey &&
      hasRaceResults(
        selectedTrack.name,
        selectedRace.number,
        formatDateForQuery(selectedRace.date),
        historicalData,
      )
    );

  // Updated getTrackStateFromData to take trackData as a parameter to avoid dependency issues
  const getTrackStateFromData = (trackName, currentTrackData = trackData) => {
    if (!trackName) return ""; // Add safety check for undefined trackName

    // Clean up track name to match the format in track-names.json
    // Remove state in parentheses if present
    const cleanTrackName = trackName.replace(/\s*\([^)]*\)\s*$/, "").trim();

    // Try to find the track in the track data
    if (currentTrackData && currentTrackData[cleanTrackName]) {
      return currentTrackData[cleanTrackName].State || "";
    }

    // If not found, try to find a partial match
    if (currentTrackData) {
      const trackKeys = Object.keys(currentTrackData);
      for (const key of trackKeys) {
        if (cleanTrackName.includes(key) || key.includes(cleanTrackName)) {
          return currentTrackData[key].State || "";
        }

        // Check Alt Name as well
        if (
          currentTrackData[key]["alt"] &&
          (cleanTrackName.includes(currentTrackData[key]["alt"]) ||
            currentTrackData[key]["alt"].includes(cleanTrackName))
        ) {
          return currentTrackData[key].State || "";
        }
      }
    }

    // If still not found, extract state from parentheses as fallback
    const stateFromParens = getTrackState(trackName);
    return stateFromParens || "";
  };

  // Add these imports at the top if not already present
  const dependencyKey = useMemo(
    () => ({
      histDataLength: Object.keys(historicalData || {}).length,
      timePeriod,
      selectedTrackId: selectedTrack?.name,
      selectedRaceId: selectedRace?.number,
      selectedDateStr: selectedDate?.toISOString(),
    }),
    [historicalData, timePeriod, selectedTrack, selectedRace, selectedDate],
  );

  // Process historical data when it loads
  useEffect(() => {
    // Wait for hotTipMinPrice to be properly loaded before processing data
    if (isHotTipSettingsLoading) {
      return;
    }

    // State to track current processing cycle
    const currentHistDataId = JSON.stringify({
      dataLength: Object.keys(historicalData || {}).length,
      timePeriod,
      trackId: selectedTrack?.name,
      raceId: selectedRace?.number,
      date: selectedDate?.toISOString().split("T")[0],
      hotTipMinPrice, // Include hotTipMinPrice in the ID
    });

    // Skip if we've already processed exactly this data (prevents update loop)
    if (dataProcessedRef.current === currentHistDataId) {
      return;
    }

    // Set the processed flag before any state updates to break potential loops
    dataProcessedRef.current = currentHistDataId;

    // Helper to reset the UI state when no data is available
    const resetUIState = () => {
      setTrackResults([]);
      setFilteredResults([]);
      setIsMounted(false);
    };

    // If no data is available, handle gracefully
    if (!historicalData || Object.keys(historicalData).length === 0) {
      if (timePeriod === "today" && !noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
      return;
    }

    try {
      const historicalEntries = Object.entries(historicalData);

      if (historicalEntries.length === 0) {
        if (timePeriod === "today" && !noDataFlaggedRef.current) {
          noDataFlaggedRef.current = true;
          setNoDataForCurrentPeriod(true);
        }
        resetUIState();
        return;
      }

      // We have data - reset the no data flag if needed
      if (noDataFlaggedRef.current) {
        noDataFlaggedRef.current = false;
        if (noDataForCurrentPeriod) {
          setNoDataForCurrentPeriod(false);
        }
      }

      // Process data into tracks and races
      const newTrackMap = new Map();

      Object.entries(historicalData).forEach(([key, data]) => {
        const parts = key.split("-");
        const day = parts[parts.length - 3];
        const month = parts[parts.length - 2];
        const year = parts[parts.length - 1];
        const raceDate = new Date(year, month - 1, day);

        const raceNumber = parts[parts.length - 4];
        const trackName = parts.slice(0, parts.length - 4).join(" ");

        // Initialize track if not exists
        if (!newTrackMap.has(trackName)) {
          // Make sure to set the state when track is first created
          const trackState = getTrackStateFromData(trackName);
          newTrackMap.set(trackName, {
            name: trackName,
            wins: 0,
            places: 0,
            races: new Map(),
            state: trackState || "",
            topTips: 0, // Initialize topTips counter
          });
        }

        const track = newTrackMap.get(trackName);

        // Count wins and places
        if (data.podium) {
          data.podium.forEach((result, index) => {
            if (index < 2) {
              if (result === "winnerWinnerChickenDinner") {
                track.wins++;
              } else if (result === "PL") {
                track.places++;
              }
            }
          });
        }

        // Check if this is a top tip - using the loaded hotTipMinPrice value
        const isTopTip =
          hotTipMinPrice > 0 &&
          data?.predictionData?.firstFourData?.length > 0 &&
          data.predictionData.firstFourData[0]?.currentPrice &&
          parseFloat(data.predictionData.firstFourData[0].currentPrice) >
            hotTipMinPrice;

        // Update top tips count for track
        if (isTopTip) {
          track.topTips = (track.topTips || 0) + 1;
        }

        // Add race data
        if (!track.races.has(raceNumber)) {
          track.races.set(raceNumber, {
            number: raceNumber,
            wins: 0,
            places: 0,
            data: data,
            date: raceDate,
            key: key,
            isTopTip: isTopTip, // Add isTopTip flag to race
          });
        }

        const race = track.races.get(raceNumber);

        // Count race-specific wins and places
        if (data.podium) {
          data.podium.forEach((result, index) => {
            if (index < 2) {
              if (result === "winnerWinnerChickenDinner") {
                race.wins++;
              } else if (result === "PL") {
                race.places++;
              }
            }
          });
        }
      });

      // Handle no tracks edge case
      if (newTrackMap.size === 0) {
        if (timePeriod === "today" && !noDataFlaggedRef.current) {
          noDataFlaggedRef.current = true;
          setNoDataForCurrentPeriod(true);
        }
        resetUIState();
        return;
      }

      // Handle custom date - add to recent dates if needed
      if (timePeriod === "custom" && newTrackMap.size > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const normalizedSelectedDate = new Date(selectedDate);
        normalizedSelectedDate.setHours(0, 0, 0, 0);

        if (
          normalizedSelectedDate.getTime() !== today.getTime() &&
          normalizedSelectedDate.getTime() !== yesterday.getTime()
        ) {
          const formattedDate = format(selectedDate, "dd/MM/yyyy");

          // Check if date already exists in recentDates to prevent duplication
          const dateExists = recentDates.some(
            (d) => format(d.date, "dd/MM/yyyy") === formattedDate,
          );

          // Check if the date is among the automatically generated history day buttons
          const isInPredefinedDays = [...Array(historyDaysLoaded)].some(
            (_, i) => {
              const predefinedDate = new Date();
              predefinedDate.setDate(predefinedDate.getDate() - (i + 2)); // i+2 to start 2 days ago (after yesterday)
              predefinedDate.setHours(0, 0, 0, 0);
              return (
                predefinedDate.getTime() === normalizedSelectedDate.getTime()
              );
            },
          );

          // Only add to recentDates if it's not a duplicate and not in the predefined 7-day range
          if (!dateExists && !isInPredefinedDays) {
            const dateObj = { date: selectedDate, formatted: formattedDate };
            // Update recentDates - add the new date and sort properly
            setRecentDates((prevDates) => {
              const newDates = [...prevDates, dateObj];
              return newDates
                .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort in reverse chronological order
                .slice(0, MAX_RECENT_DATES);
            });
          }
        }
      }

      // Prepare track data for display
      const tracksArray = Array.from(newTrackMap.values());

      // Convert race maps to arrays and ensure all tracks have a state
      tracksArray.forEach((track) => {
        // Make sure track state is set correctly
        if (!track.state || track.state === "") {
          track.state = getTrackStateFromData(track.name);
        }
        track.races = Array.from(track.races.values()).sort(
          (a, b) => parseInt(a.number) - parseInt(b.number),
        );
      });

      // Sort tracks by popularity and state as defined
      const sortedTracksArray = sortTracksByPopularityAndState(tracksArray);

      // Set track names for navigation
      const trackNames = sortedTracksArray.map((track) => track.name);

      // Update historical data flag if needed
      if (selectedTrack && selectedRace) {
        const today = format(new Date(), "dd-MM-yyyy");
        const trackName =
          typeof selectedTrack === "string"
            ? selectedTrack
            : selectedTrack.name;
        const raceNumber =
          typeof selectedRace === "string" ? selectedRace : selectedRace.number;
        const currentRaceKey = `${trackName.replace(/\s+/g, "-")}-${raceNumber}-${today}`;
        setHasHistoricalData(
          Object.keys(historicalData).includes(currentRaceKey),
        );
      }

      // Update track data state at the end
      setTracks(trackNames);
      setTrackResults(sortedTracksArray);
      setFilteredResults(sortedTracksArray);

      if (!selectedTrack && !selectedRace) {
        setBreadcrumbs([{ name: "All Tracks", path: null }]);
      }
    } catch (error) {
      logger.error("Error processing historical data:", error);
      if (timePeriod === "today" && !noDataFlaggedRef.current) {
        noDataFlaggedRef.current = true;
        setNoDataForCurrentPeriod(true);
      }
      resetUIState();
    }
  }, [
    historicalData,
    trackData,
    timePeriod,
    selectedTrack,
    selectedRace,
    selectedDate,
    hotTipMinPrice,
    isHotTipSettingsLoading,
  ]);

  // Filter results when search query changes
  useEffect(() => {
    if (!trackResults.length) return;

    if (!searchQuery.trim()) {
      setFilteredResults(trackResults);
      return;
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = trackResults.filter((track) =>
      track.name.toLowerCase().includes(query),
    );

    setFilteredResults(filtered);
  }, [searchQuery, trackResults]);

  // Extract state from track name (e.g., "Ascot (WA)" -> "WA")
  const getTrackState = (trackName) => {
    const match = trackName.match(/\(([^)]+)\)/);
    return match ? match[1] : "";
  };

  // Helper function to sort tracks by popularity and state
  const sortTracksByPopularityAndState = (tracksArray) => {
    // Define an ordering for states
    const stateOrder = {
      VIC: 1,
      NSW: 2,
      QLD: 3,
      SA: 4,
      WA: 5,
      TAS: 6,
      Other: 7,
    };

    // Define popular metro tracks to prioritize - flattened list with global priority
    const popularMetroTracks = {
      VIC: [
        "Flemington",
        "Caulfield",
        "Moonee Valley",
        "Sandown",
        "Sandown Lakeside",
        "Sandown Hillside",
      ],
      NSW: ["Randwick", "Rosehill", "Kensington", "Canterbury", "Warwick Farm"],
      QLD: ["Eagle Farm", "Doomben"],
      SA: ["Morphettville", "Morphettville Parks", "Balaklava"],
      WA: ["Ascot", "Belmont"],
      TAS: ["Hobart", "Launceston"],
    };

    // Create a global priority list for popular metro tracks
    const globalMetroPriority = [];
    Object.keys(stateOrder).forEach((state) => {
      if (popularMetroTracks[state]) {
        popularMetroTracks[state].forEach((track) => {
          globalMetroPriority.push(track);
        });
      }
    });

    // Helper to check if a track is metro
    const isMetroTrack = (track) => {
      const state = track.state || "Other";
      const metroList = popularMetroTracks[state] || [];
      return metroList.some(
        (metroTrack) =>
          track.name === metroTrack ||
          // Handle variations in Moonee Valley naming
          (metroTrack === "Moonee Valley" && track.name.includes("Moonee")),
      );
    };

    // Helper to get global metro priority
    const getGlobalMetroPriority = (track) => {
      // Find exact match first
      const exactIndex = globalMetroPriority.findIndex((m) => track.name === m);
      if (exactIndex !== -1) return exactIndex;

      // Handle Moonee Valley variations
      if (track.name.includes("Moonee")) {
        const mooneeIndex = globalMetroPriority.findIndex(
          (m) => m === "Moonee Valley",
        );
        if (mooneeIndex !== -1) return mooneeIndex;
      }

      return globalMetroPriority.length; // Put at the end if not found
    };

    // Sort the tracks: popular metro tracks first (across all states), then by state order
    return tracksArray.sort((a, b) => {
      const isMetroA = isMetroTrack(a);
      const isMetroB = isMetroTrack(b);

      // If both are metro tracks, sort by their global priority
      if (isMetroA && isMetroB) {
        return getGlobalMetroPriority(a) - getGlobalMetroPriority(b);
      }

      // Metro tracks come before non-metro tracks
      if (isMetroA && !isMetroB) return -1;
      if (!isMetroA && isMetroB) return 1;

      // For non-metro tracks, sort by state first, then alphabetically
      const stateA = a.state || "Other";
      const stateB = b.state || "Other";

      if (stateA !== stateB) {
        return stateOrder[stateA] - stateOrder[stateB];
      }

      // Within the same state for non-metro tracks, sort alphabetically
      return a.name.localeCompare(b.name);
    });
  };

  // Update selectedDate when timePeriod changes
  useEffect(() => {
    const today = new Date();
    if (timePeriod === "today") {
      setSelectedDate(today);
    } else if (timePeriod === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      setSelectedDate(yesterday);
    }

    // Scroll active date into view whenever timePeriod changes
    scrollActiveDateIntoView();
  }, [timePeriod, scrollActiveDateIntoView]);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (breadcrumb) => {
    if (breadcrumb.path) {
      navigate(breadcrumb.path);
      setSelectedTrack(null);
      setSelectedRace(null);
      setBreadcrumbs([]);
      // Reset view mode to all-tracks
      setViewMode("all-tracks");
    } else if (breadcrumb.onClick) {
      breadcrumb.onClick();
      // View mode will be updated by the onClick function (selectTrack)
    }
  };

  // Get result display for a horse
  const getResultDisplay = (horse, raceData) => {
    if (!raceData || !raceData.podium) return null;

    const podiumValue = raceData.podium[horse.rank - 1];

    logger.log("🔍 [results.jsx] Checking horse result:", {
      horseName: horse?.name,
      horseNumber: horse?.number,
      horseRank: horse?.rank,
      podiumIndex: horse.rank - 1,
      podiumValue,
      fullPodium: raceData.podium,
      isScratched: podiumValue === "SCRATCHED" || podiumValue === "SCR",
    });

    // Check if the horse was scratched
    if (podiumValue === "SCRATCHED" || podiumValue === "SCR") {
      logger.log("✅ [results.jsx] Horse is SCRATCHED:", horse?.name);
      return <span className="text-xs text-gray-500 font-bold">SCR</span>;
    }
    // Check if the horse was a winner
    else if (podiumValue === "winnerWinnerChickenDinner") {
      return <TrophyIcon fill="#F30099" className="w-5 h-5" />;
    }
    // Check if the horse was placed
    else if (podiumValue === "PL") {
      return <MedalIcon className="w-5 h-5" />;
    }
    // Otherwise, unplaced
    else {
      return <span className="text-xs text-gray-500">UPL</span>;
    }
  };

  // Add this useEffect to log only once after initial render
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    }
  }, [selectedTrack, selectedRace, timePeriod, filteredResults]);

  // Original functions that were removed
  const originalHandleRaceNavigation = (newTrack, newRace) => {
    // Find the track object that matches the track name
    const trackObj = filteredResults.find((track) => track.name === newTrack);
    if (!trackObj) return;

    // Find the race object that matches the race number
    const raceObj = trackObj.races.find(
      (race) => race.number.toString() === newRace.toString(),
    );
    if (!raceObj) return;

    // Update the selected track and race
    setSelectedTrack(trackObj);
    setSelectedRace(raceObj);

    // Update breadcrumbs
    setBreadcrumbs([
      { name: "All Tracks", path: "/results" },
      {
        name: trackObj.name,
        path: null,
        onClick: () => {
          // Inline version of selectTrack to avoid reference issues
          setSelectedTrack(trackObj);
          setBreadcrumbs([
            { name: "All Tracks", path: "/results" },
            {
              name: `${trackObj.name} (G)`,
              path: null,
              onClick: () => selectTrack(trackObj),
            },
          ]);
          setViewMode("track-races");
          setSelectedRace(null);
        },
      },
      { name: `Race ${raceObj.number}`, path: null },
    ]);
  };

  const today = new Date();

  const createLeaderboardQueryData = (data) => {
    const leaderboardQueryData = [];
    data.forEach((horse, index) => {
      // i need to create name and number from formattedName
      const number = horse?.formattedName?.split(".")[0];
      const name = horse?.formattedName?.split(".").slice(1).join(" ");
      const selectionId = horse?.selectionId;

      leaderboardQueryData.push({
        name,
        number,
        selectionId,
        ...horse,
        jockey:
          typeof horse.jockey === "object"
            ? horse.jockey?.fullName || horse.jockey?.name || null
            : horse.jockey,
        trainer:
          typeof horse.trainer === "object"
            ? horse.trainer?.fullName || horse.trainer?.name || null
            : horse.trainer,
      });
    });
    return leaderboardQueryData;
  };

  // Add function to check for early predictions
  const checkHasEarlyPredictions = (
    track,
    race,
    date,
    leaderboardData,
    earlyData,
  ) => {
    if (!track || !race || !date || !leaderboardData) {
      if (!earlyData || !leaderboardData.earlyPredictionData) {
        return false;
      }
    }

    const raceKey = `${track.replace(/\s+/g, "-")}-${race}-${formatDateForQuery(date)}`;

    // Get early predictions for this race
    const earlyPredictionsForRace = earlyData[raceKey]?.firstFourData;

    // Get tipUpdatedAt values for both datasets
    const regularTipUpdatedAt = leaderboardData?.tipUpdatedAt;
    const earlyTipUpdatedAt = earlyData[raceKey]?.tipUpdatedAt;

    if (leaderboardData?.earlyPredictionData?.firstFourData !== "N/A") {
      return true;
    } else if (earlyPredictionsForRace) {
      return true;
    } else {
      return false;
    }

    // Return false if tipUpdatedAt values are the same or no early predictions exist
    if (
      regularTipUpdatedAt === earlyTipUpdatedAt ||
      !Array.isArray(earlyPredictionsForRace) ||
      earlyPredictionsForRace.length === 0
    ) {
      return false;
    }

    return true;
  };

  // Render error state
  if (isError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p className="text-red-500">Error loading results</p>
      </div>
    );
  }

  // Check if there are no results
  const noResults = !filteredResults.length;
  return (
    <div className="results-page-container min-h-dvh h-dvh md:h-screen bg-gray-50 pb-16">
      <div className="max-w-md mx-auto h-full flex flex-col">
        {/* Time Period Filters */}
        <div
          ref={dateFilterContainerRef}
          className="date-filter-container px-4 pt-4 pb-2 bg-white overflow-x-auto overflow-y-hidden"
        >
          <div className="flex flex-row-reverse gap-2 min-w-max">
            <button
              type="button"
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                timePeriod === "today"
                  ? "bg-[#F30099] text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
              onClick={() => handleTimeChange("today")}
              data-gtm-element-id="results_date-filter_button-today"
            >
              TODAY
            </button>

            <button
              type="button"
              className={`px-4 py-2 rounded-full text-sm font-medium ${
                timePeriod === "yesterday"
                  ? "bg-[#F30099] text-white"
                  : "bg-gray-200 text-gray-700"
              }`}
              onClick={() => handleTimeChange("yesterday")}
              data-gtm-element-id="results_date-filter_button-yesterday"
            >
              YESTERDAY
            </button>

            {/* Previous days buttons - dynamically generated based on historyDaysLoaded */}
            {[...Array(historyDaysLoaded)].map((_, i) => {
              const date = new Date();
              date.setDate(date.getDate() - (i + 2)); // i+2 to start 2 days ago (after yesterday)
              const dateStr = format(date, "dd/MM/yyyy");
              const displayStr = format(date, "dd MMM");

              // Check if this is one of the new buttons being loaded
              const isNewButton = loadingMoreDays && i >= historyDaysLoaded - 7;
              const isCurrentlyLoading =
                isNewButton && i - (historyDaysLoaded - 7) < loadingDayIndex;

              return (
                <button
                  key={dateStr}
                  type="button"
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                    timePeriod === "custom" &&
                    format(selectedDate, "dd/MM/yyyy") === dateStr
                      ? "bg-[#F30099] text-white"
                      : isCurrentlyLoading
                        ? "bg-gray-300 animate-pulse"
                        : "bg-gray-200 text-gray-700"
                  } ${isNewButton && !isCurrentlyLoading && loadingDayIndex > i - (historyDaysLoaded - 7) ? "opacity-0 animate-fade-in" : ""}`}
                  onClick={() =>
                    !isCurrentlyLoading && handleTimeChange("custom", date)
                  }
                  disabled={isCurrentlyLoading}
                  data-gtm-element-id={`results_date-filter_button-${format(date, "dd-MM")}`}
                  style={{
                    animationDelay:
                      isNewButton &&
                      !isCurrentlyLoading &&
                      loadingDayIndex > i - (historyDaysLoaded - 7)
                        ? `${(i - (historyDaysLoaded - 7)) * 100}ms`
                        : "0ms",
                  }}
                >
                  {isCurrentlyLoading ? (
                    <span className="text-transparent">{displayStr}</span>
                  ) : (
                    displayStr
                  )}
                </button>
              );
            })}

            {/* Load More button - shows at the left end */}
            <button
              type="button"
              className={`px-3 py-2 rounded-full text-xs font-medium border border-dashed transition-all duration-200 whitespace-nowrap ${
                loadingMoreDays
                  ? "border-gray-300 bg-gray-100 text-gray-500 cursor-not-allowed"
                  : "border-gray-400 bg-white text-gray-600 hover:border-[#F30099] hover:text-[#F30099] hover:bg-pink-50"
              }`}
              onClick={loadMoreDays}
              disabled={loadingMoreDays}
              data-gtm-element-id="results_date-filter_load-more-button"
            >
              {loadingMoreDays ? (
                <div className="w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <span>Load more</span>
              )}
            </button>

            {/* Recent dates, sorted chronologically (newest to oldest) */}
            {recentDates
              .sort((a, b) => new Date(b.date) - new Date(a.date)) // Sort in reverse chronological order
              .map((item) => (
                <button
                  key={item.formatted}
                  type="button"
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    timePeriod === "custom" &&
                    format(selectedDate, "dd/MM/yyyy") === item.formatted
                      ? "bg-[#F30099] text-white"
                      : "bg-gray-200 text-gray-700"
                  }`}
                  onClick={() =>
                    handleTimeChange("custom", new Date(item.date))
                  }
                  data-gtm-element-id={`results_date-filter_button-${format(new Date(item.date), "dd-MM")}`}
                >
                  {format(new Date(item.date), "dd MMM")}
                </button>
              ))}
          </div>
        </div>

        {isLoading || isEarlyPredictionsLoading ? (
          <div className="flex h-full flex-1 items-center justify-center">
            <Spinner size="large" className="text-[#F30099]" />
          </div>
        ) : (
          <>
            {/* Breadcrumbs */}
            <div className="breadcrumbs-container">
              {breadcrumbs.length > 0 && (
                <div
                  className={`breadcrumbs-wrapper bg-white flex items-center space-x-2 text-sm ${breadcrumbs.length == 1 ? " breadcrumb-root" : ""}`}
                >
                  {breadcrumbs.map((crumb, index) => (
                    <div key={index} className="flex items-center">
                      {index > 0 && (
                        <ChevronRight className="w-4 h-4 mx-1 text-gray-400" />
                      )}
                      <span
                        className={`${crumb.path || crumb.onClick ? "text-[#F30099] cursor-pointer" : "text-gray-700"}`}
                        onClick={() =>
                          crumb.path || crumb.onClick
                            ? handleBreadcrumbClick(crumb)
                            : null
                        }
                        data-gtm-element-id={
                          crumb.path || crumb.onClick
                            ? `results_breadcrumb_link-${index}`
                            : undefined
                        }
                      >
                        {crumb.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {breadcrumbs.length < 3 && (
                <div className="flex items-center gap-3">
                  {/* Custom Sort Dropdown */}
                  <div className="relative sort-dropdown">
                    <Button
                      onClick={toggleDropdown}
                      variant="outline"
                      size="sm"
                      className="w-[140px] h-8 px-3 py-1 text-xs justify-between bg-white border-2 border-gray-400 hover:bg-gray-50 hover:border-gray-500 shadow-sm"
                    >
                      <span>{getSortDisplayLabel(sortType)}</span>
                      <div className="flex items-center gap-1">
                        {sortType !== "popularity" &&
                          getSortArrowIcon(sortType, sortDirection, true)}
                        <ChevronDown
                          className={`w-3 h-3 transition-transform ${isDropdownOpen ? "rotate-180 text-[#F30099]" : "text-gray-500"}`}
                        />
                      </div>
                    </Button>

                    {isDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 w-[140px] bg-white border-2 border-gray-400 rounded-md shadow-lg z-50">
                        <div className="py-1">
                          <button
                            onClick={() => handleSortChange("popularity")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "popularity"
                                ? "bg-[#F30099]/10 text-[#F30099]"
                                : ""
                            }`}
                          >
                            <span>Popularity</span>
                          </button>
                          <button
                            onClick={() => handleSortChange("a-z")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "a-z"
                                ? "bg-[#F30099]/10 text-[#F30099]"
                                : ""
                            }`}
                          >
                            <span>Alphabetical</span>
                            <div className="ml-2">
                              {getSortArrowIcon(
                                "a-z",
                                sortDirection,
                                sortType === "a-z",
                              )}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("winners")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "winners"
                                ? "bg-[#F30099]/10 text-[#F30099]"
                                : ""
                            }`}
                          >
                            <span>Wins</span>
                            <div className="ml-2">
                              {getSortArrowIcon(
                                "winners",
                                sortDirection,
                                sortType === "winners",
                              )}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("places")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "places"
                                ? "bg-[#F30099]/10 text-[#F30099]"
                                : ""
                            }`}
                          >
                            <span>Places</span>
                            <div className="ml-2">
                              {getSortArrowIcon(
                                "places",
                                sortDirection,
                                sortType === "places",
                              )}
                            </div>
                          </button>
                          <button
                            onClick={() => handleSortChange("both")}
                            className={`w-full px-3 py-2 text-xs text-left hover:bg-gray-100 flex items-center justify-between ${
                              sortType === "both"
                                ? "bg-[#F30099]/10 text-[#F30099]"
                                : ""
                            }`}
                          >
                            <span>Total</span>
                            <div className="ml-2">
                              {getSortArrowIcon(
                                "both",
                                sortDirection,
                                sortType === "both",
                              )}
                            </div>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <ResultsTally
                    historicalData={historicalData}
                    trackName={selectedTrack?.name}
                    iconSize="w-5 h-5"
                    textSize="text-sm"
                    tipCount={2}
                  />
                </div>
              )}
            </div>

            {/* Results Content */}
            <div className="results-content-container px-0 pt-2 pb-[13rem] flex-1 min-h-0 overflow-y-auto space-y-2 border-t border-gray-200">
              {noResults || noDataForCurrentPeriod ? (
                <div className="no-results-container h-full flex flex-col items-center justify-center py-10">
                  <Frown className="w-12 h-12 text-gray-400 mb-2" />
                  <p className="text-md font-medium text-gray-700">
                    {noDataForCurrentPeriod
                      ? "No data available yet"
                      : `No results for ${timePeriod}`}
                  </p>
                  <p className="text-sm text-gray-500 text-center px-4">
                    {noDataForCurrentPeriod
                      ? `Data for ${format(selectedDate, "dd MMM yyyy")} is not yet available.`
                      : "Please check again later or try another date."}
                  </p>

                  <div className="mt-6 flex flex-col items-center">
                    <p className="text-sm mb-2">Try a different date:</p>
                    <div className="flex space-x-3">
                      {timePeriod !== "yesterday" && (
                        <button
                          className="px-6 py-2 bg-[#F30099] text-white rounded-full text-sm font-medium"
                          onClick={() => handleTimeChange("yesterday")}
                          data-gtm-element-id="results_empty-state_button-yesterday"
                        >
                          YESTERDAY
                        </button>
                      )}
                      {timePeriod !== "today" && (
                        <button
                          className="px-6 py-2 bg-[#F30099] text-white rounded-full text-sm font-medium"
                          onClick={() => handleTimeChange("today")}
                          data-gtm-element-id="results_empty-state_button-today"
                        >
                          TODAY
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : selectedTrack && !selectedRace ? (
                <div className="render-races-container day-specific-container space-y-2">
                  {selectedTrack.races.map((race) => (
                    <div
                      key={`${selectedTrack.name}-${race.number}`}
                      className=" bg-white rounded-lg shadow p-4 cursor-pointer mx-4"
                      onClick={() => selectRace(selectedTrack, race)}
                      data-gtm-element-id={`results_race_button-${selectedTrack.name.replace(/\s+/g, "-").toLowerCase()}-${race.number}`}
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-2">
                          <span className="font-medium">
                            Race {race.number}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4">
                          {race.isTopTip && (
                            <div className="flex items-center">
                              <div className="bg-[#FFEBBB] rounded-full px-2 py-0.5 flex items-center">
                                <FlameIcon className="w-4 h-4" />
                                <span className="ml-1 text-xs text-[#A0200C] font-bold">
                                  {(() => {
                                    // Check if this race has results
                                    const raceKey = `${selectedTrack?.name.replace(/\s+/g, "-")}-${race.number}-${format(today, "dd-MM-yyyy")}`;
                                    const raceResults = historicalData[raceKey];
                                    // If we have results and the top tip is the winner
                                    if (
                                      raceResults?.betfairResults?.winner &&
                                      race.data?.predictionData?.firstFourData
                                        ?.length > 0
                                    ) {
                                      const topTipSelectionId =
                                        race.data.predictionData
                                          .firstFourData[0]?.selectionId;
                                      const winnerSelectionId =
                                        raceResults.betfairResults.winner
                                          .selectionId;

                                      // If the top tip's selectionId matches the winner's selectionId
                                      if (
                                        topTipSelectionId === winnerSelectionId
                                      ) {
                                        return "Hot Tip";
                                      }
                                    }

                                    return "Top Tip";
                                  })()}
                                </span>
                              </div>
                            </div>
                          )}
                          {race.wins > 0 && (
                            <div className="flex items-center">
                              <TrophyIcon fill="#F30099" className="w-5 h-5" />
                              <span className="ml-1 text-sm">x{race.wins}</span>
                            </div>
                          )}
                          {race.places > 0 && (
                            <div className="flex items-center">
                              <MedalIcon className="w-5 h-5" />
                              <span className="ml-1 text-sm">
                                x{race.places}
                              </span>
                            </div>
                          )}
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : selectedTrack && selectedRace ? (
                <div className="render-race-container race-specific-container">
                  <div className="space-y-2">
                    {isLexiSocial && (
                      <div className="mx-2 mt-2 mb-4 px-3 py-3 rounded-lg border border-pink-200 bg-pink-50 text-sm text-pink-900 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Lock
                            className="w-5 h-5 text-pink-700"
                            aria-hidden="true"
                          />
                          <span className="font-semibold">
                            Unlock race-time tips by upgrading
                          </span>
                        </div>
                        <button
                          className="text-xs font-semibold text-white bg-[#F30099] hover:bg-pink-700 rounded-md px-3 py-2 transition-colors"
                          onClick={triggerUpgradeFlow}
                        >
                          Upgrade
                        </button>
                      </div>
                    )}

                    <div className="tips-master-container pt-3 pb-1 px-3 mx-2 mt-2 mb-[2px] rounded-lg shadow-[0_0_3px_rgba(0,0,0,0.25)] bg-white transition-all duration-500">
                      {selectedRace &&
                      selectedRace.data &&
                      selectedRace.data.predictionData &&
                      // Check if regular prediction data is available and valid
                      ((Array.isArray(
                        selectedRace.data.predictionData.firstFourData,
                      ) &&
                        selectedRace.data.predictionData.firstFourData.length >
                          0) ||
                        // OR check if early prediction data is available and valid
                        (selectedRace.data.earlyPredictionData &&
                          Array.isArray(
                            selectedRace.data.earlyPredictionData.firstFourData,
                          ) &&
                          selectedRace.data.earlyPredictionData.firstFourData
                            .length > 0)) ? (
                        (() => {
                          // Determine which data source to use
                          const hasValidRegularData =
                            Array.isArray(
                              selectedRace.data.predictionData.firstFourData,
                            ) &&
                            selectedRace.data.predictionData.firstFourData
                              .length > 0;
                          const hasValidEarlyData =
                            selectedRace.data.earlyPredictionData &&
                            Array.isArray(
                              selectedRace.data.earlyPredictionData
                                .firstFourData,
                            ) &&
                            selectedRace.data.earlyPredictionData.firstFourData
                              .length > 0;

                          let leaderboardData = [];
                          let hasEarlyPredictions = false;
                          let modifiedRaceData = { ...selectedRace.data };
                          const formatRaceStart = () => {
                            const candidates = [
                              selectedRace.data?.raceInfo?.startTime,
                              selectedRace.data?.raceStartTime,
                              selectedRace.data?.race_start_time,
                              selectedRace.data?.startTime,
                              selectedRace.data?.start_time,
                              selectedRace.data?.marketStartTime,
                              selectedRace.data?.market_start_time,
                              selectedRace.data?.betfairResults?.startTime,
                              selectedRace.data?.betfairResults?.raceStartTime,
                              selectedRace.data?.betfairResults?.raceStart,
                            ].filter(Boolean);
                            for (const candidate of candidates) {
                              const parsed =
                                typeof candidate === "string"
                                  ? new Date(candidate)
                                  : new Date(candidate);
                              if (!Number.isNaN(parsed.valueOf())) {
                                try {
                                  return format(parsed, "h:mm a");
                                } catch {
                                  continue;
                                }
                              }
                            }
                            return null;
                          };
                          const raceStartTimeFormatted = formatRaceStart();

                          if (hasValidRegularData && hasValidEarlyData) {
                            // Both data sources exist - use regular as main, early as comparison
                            leaderboardData = createLeaderboardQueryData(
                              selectedRace.data.predictionData.firstFourData,
                            );
                            hasEarlyPredictions = true;
                          } else if (hasValidRegularData) {
                            // Only regular data exists
                            leaderboardData = createLeaderboardQueryData(
                              selectedRace.data.predictionData.firstFourData,
                            );
                            hasEarlyPredictions = false;
                          } else if (hasValidEarlyData) {
                            // Only early data exists - use it as main data and clear early predictions to avoid duplication
                            leaderboardData = createLeaderboardQueryData(
                              selectedRace.data.earlyPredictionData
                                .firstFourData,
                            );
                            hasEarlyPredictions = false;
                            // Modify the race data to put early data in the main prediction slot
                            modifiedRaceData = {
                              ...selectedRace.data,
                              predictionData: {
                                ...selectedRace.data.predictionData,
                                firstFourData:
                                  selectedRace.data.earlyPredictionData
                                    .firstFourData,
                              },
                            };
                          } else {
                            // No valid data
                            leaderboardData = [];
                            hasEarlyPredictions = false;
                          }

                          return (
                            <ErrorBoundary>
                              <RenderTips
                                externalLeaderboardQueryData={modifiedRaceData}
                                externalLeaderboardData={leaderboardData}
                                externalHistoricalData={historicalData}
                                externalHasHistoricalData={true}
                                externalHasEarlyPredictions={
                                  hasEarlyPredictions
                                }
                                externalEarlyPredictionsData={
                                  hasEarlyPredictions
                                    ? earlyPredictionsData
                                    : {}
                                }
                                selectedTrack={selectedTrack.name}
                                selectedRace={selectedRace.number}
                                today={formatDateForQuery(selectedRace.date)}
                                isChanging={false}
                                isMounted={true}
                                isTipHistoryExpanded={isTipHistoryExpanded}
                                toggleTipHistory={toggleTipHistory}
                                isResultsPage={true}
                                raceStartTimeOverride={raceStartTimeFormatted}
                                tipMovementSources={raceTipMovementSources}
                                hideTipMovement={hideTipMovement}
                                onTipMovementShownChange={
                                  setIsTipMovementShown
                                }
                                showPriceToggle={true}
                              />
                            </ErrorBoundary>
                          );
                        })()
                      ) : (
                        <div className="py-8 text-center">
                          <AlertTriangle className="mx-auto h-10 w-10 text-yellow-500 mb-3" />
                          <p className="text-lg font-medium">
                            No tips data available
                          </p>
                          <p className="text-sm text-gray-500 mt-1">
                            The prediction data for this race couldn't be loaded
                          </p>
                          <p className="text-xs text-gray-500 mt-3">
                            {selectedRace
                              ? `Race ${selectedRace.number}`
                              : "No race selected"}
                          </p>
                        </div>
                      )}

                      {/* Early Mail in the same container, under Late Mail / Race Day tips (as /todays-tips) */}
                      {canShowEarlyMail && (
                        <EarlyMailRaceSection
                          earlyMail={earlyMail}
                          isOpen={isEarlyMailOpen}
                          onToggle={() => setIsEarlyMailOpen((v) => !v)}
                          trackName={selectedTrack.name}
                          raceNumber={selectedRace.number}
                          date={formatDateForQuery(selectedRace.date)}
                          historicalData={historicalData}
                          isResultsPage={true}
                          tipMovementSources={earlyMailTipMovementSources}
                          hideTipMovement={hideTipMovement}
                          limitHeight={false}
                          embedded={true}
                          gtmId="results_early-mail_button"
                        />
                      )}

                      {SHOW_TIP_MARKER_KEY && isTipMovementShown && (
                        <TipMovementKey />
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="render-tracks-container tracks-specific-container flex flex-col gap-2 mx-4"
                  title={`${selectedTrack?.name}`}
                >
                  {(() => {
                    // Apply sorting to tracks
                    const sortedTracks = sortTracks(
                      filteredResults,
                      sortType,
                      sortDirection,
                    );
                    logger.log(
                      `📊 RESULTS: Applying sort: ${sortType} (${sortDirection}) to ${sortedTracks.length} tracks`,
                    );

                    return sortedTracks.map((track) => (
                      <div
                        key={track.name}
                        className="track-button-container bg-white rounded-lg shadow"
                      >
                        <div
                          className="p-4 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            selectTrack(track);
                          }}
                          data-gtm-element-id={`results_track_button-${track.name.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <div className="flex justify-between items-center">
                            <div className="track-name-state-container">
                              <span className="track-name-text font-medium">
                                {track.name}
                              </span>
                              {track.state && track.state.trim() !== "" && (
                                <span className="track-state-text text-sm text-gray-500 ml-2">
                                  ({track.state})
                                </span>
                              )}
                            </div>
                            <div className="track-badges-container flex items-center space-x-4">
                              {track.topTips > 0 && (
                                <div className="top-tip-container flex items-center">
                                  <div
                                    className={`${track.topTips > 1 ? "bg-[#FFEBBB] rounded-full px-2 py-0.5 flex items-center" : "flex items-center"}`}
                                  >
                                    <FlameIcon className="w-4 h-4" />
                                    {track.topTips > 1 && (
                                      <span className="ml-1 text-xs text-[#A0200C] font-bold">
                                        x{track.topTips}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                              {track.wins > 0 && (
                                <div className="trophy-container flex items-center">
                                  <TrophyIcon
                                    fill="#F30099"
                                    className="w-5 h-5"
                                  />
                                  <span className="ml-1 text-sm">
                                    x{track.wins}
                                  </span>
                                </div>
                              )}
                              {track.places > 0 && (
                                <div className="medal-container flex items-center">
                                  <MedalIcon className="w-5 h-5" />
                                  <span className="ml-1 text-sm">
                                    x{track.places}
                                  </span>
                                </div>
                              )}
                              <ChevronRight
                                className={`w-5 h-5 text-gray-400 transform transition-transform ${
                                  expandedTracks[track.name] ? "rotate-90" : ""
                                }`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            <RaceNavbar
              selectedTrack={selectedTrack?.name || null}
              selectedRace={selectedRace?.number || null}
              tracks={tracks}
              trackMap={trackMap}
              onNavigate={originalHandleRaceNavigation}
              selectedDate={selectedDate}
              availableDates={availableDates}
              onDateChange={handleDateChange}
              viewMode={viewMode}
              customOnNavigate={handleRaceNavigation}
            />
          </>
        )}
      </div>

      <BottomNavbar />
    </div>
  );
}
